import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { PLATFORM_ORG_ID, createAuthHeaders } from '../../shared/api/auth';
import { API_URL } from '../../config';

export interface OrgMembership {
  organizationId: string;
  organizationName?: string;
  role: 'admin' | 'member' | 'editor';
  status: string;
}

interface OrganizationContextValue {
  activeOrgId: string;
  organizations: OrgMembership[];
  setOrganizations: (orgs: OrgMembership[]) => void;
  isOrgAdmin: boolean;
  orgEnabledModules: string[] | null;
  orgNotifPrefs: Record<string, boolean> | null;
  setOrgNotifPrefs: (prefs: Record<string, boolean> | null) => void;
  /** Who is signed in (null when nobody): the modules and switches are read only for them. */
  setSignedInUserId: (userId: string | null) => void;
}

const OrganizationContext = createContext<OrganizationContextValue>({
  activeOrgId: PLATFORM_ORG_ID,
  organizations: [],
  setOrganizations: () => {},
  isOrgAdmin: false,
  orgEnabledModules: null,
  orgNotifPrefs: null,
  setOrgNotifPrefs: () => {},
  setSignedInUserId: () => {},
});

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  // One space for everyone (ADR-025): nothing to switch between.
  const activeOrgId: string = PLATFORM_ORG_ID;
  const [organizations, setOrganizationsState] = useState<OrgMembership[]>([]);
  const [orgEnabledModules, setOrgEnabledModules] = useState<string[] | null>(null);
  const [orgNotifPrefs, setOrgNotifPrefs] = useState<Record<string, boolean> | null>(null);
  // Both reads need a signed-in person: on the sign-in page they were refused (403) and,
  // made only once, were not made again after signing in until a reload.
  const [signedInUserId, setSignedInUserId] = useState<string | null>(null);

  const setOrganizations = useCallback((orgs: OrgMembership[]) => {
    setOrganizationsState(orgs);
  }, []);

  useEffect(() => {
    if (!activeOrgId || !signedInUserId) {
      setOrgEnabledModules(null);
      setOrgNotifPrefs(null);
      return;
    }

    fetch(`${API_URL}/api/organizations/${activeOrgId}/modules`, { headers: createAuthHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data && Array.isArray(data.enabledModules)) {
          setOrgEnabledModules(data.enabledModules);
        }
      })
      .catch(() => {});

    // /branding now carries only the notification switches: Kudos is one space with
    // one look (ADR-025), so per-organisation colours and logos are not read.
    fetch(`${API_URL}/api/organizations/${activeOrgId}/branding`, { headers: createAuthHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) {
          setOrgNotifPrefs(data.notificationPreferences ?? null);
        }
      })
      .catch(() => {});
  }, [activeOrgId, signedInUserId]);

  const activeMembership = organizations.find((o) => o.organizationId === activeOrgId);
  const isOrgAdmin = activeMembership?.role === 'admin';


  return (
    <OrganizationContext.Provider value={{
      activeOrgId,
      organizations,
      setOrganizations,
      isOrgAdmin,
      orgEnabledModules,
      orgNotifPrefs,
      setOrgNotifPrefs,
      setSignedInUserId,
    }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  return useContext(OrganizationContext);
}
